import { useUserStore } from '~/store/user'
import { useStore } from '~/store'
import { useBackUrl } from '~/hooks'
import isMobile from 'is-mobile'
import { meiQiaLocalMap } from '@/utils/locale'
import { inApp } from '@/utils/inApp'
import { callHandler, isiOS, nativeGoLogin } from '@/utils/JSbridge'
import { setToken } from '@/components/auth/useAuth'

export async function InitApp() {
  const user = useUserStore()
  const store = useStore()
  const route = useRoute()
  const router = useRouter()
  const localePath = useLocalePath()
  const { locale } = useI18n()
  store.loadFavContracts()
  store.getPublic()
  store.getOverview()

  if (import.meta.client) {
    getDeviceId()
    const { token: queryToken } = route.query
    if (queryToken) {
      localStorage.token = queryToken
      const query = { ...route.query }
      delete query.token
      router.replace({ query })
    }

    if (inApp) {
      const token = await callHandler({
        uri: 'request',
        method: 'getToken',
        data: '',
        success: 'onGetTokenCallback',
      })

      if (!token)
        return

      const finalToken = isiOS
        ? JSON.parse(token).token
        : token.substring(1, token.length - 1)

      if (finalToken) {
        localStorage.token = finalToken
        setToken(finalToken)
      }
    }
    user.syncCookieToken(queryToken)
    watch(() => user.isLogin, (nv) => {
      if (!nv && route.meta.needLogin) {
        if (inApp) {
          nativeGoLogin()
        }
        else {
          location.href = useBackUrl(localePath('/log-in'))
        }
      }
    }, {
      immediate: true,
    })

    // meiqia language
    watch(() => locale.value, (n) => {
      if (process.client && window?._MEIQIA) {
        window?._MEIQIA('language', meiQiaLocalMap[n] || 'en')
      }
    })

    useHead({
      script: [
        { type: 'text/javascript', innerHTML: `
                  const favicon = document.getElementById('favicon');
                  const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
                  if (prefersDarkMode) {
                    favicon.href = '/favicon_dark.ico';
                  }
              ` },
        { type: 'text/javascript', innerHTML: `
                (function(m, ei, q, i, a, j, s) {
                    m[i] = m[i] || function() {
                        (m[i].a = m[i].a || []).push(arguments)
                    };
                    j = ei.createElement(q),
                        s = ei.getElementsByTagName(q)[0];
                    j.async = true;
                    j.charset = 'UTF-8';
                    j.src = 'https://static.meiqia.com/dist/meiqia.js';
                    s.parentNode.insertBefore(j, s);
                })(window, document, 'script', '_MEIQIA');
                _MEIQIA('entId', '71b5133e8499e196a7cf21b98921e080');
                _MEIQIA('language',  '${meiQiaLocalMap[locale.value] || 'en'}');
                _MEIQIA('${inApp ? 'withoutBtn' : ''}');
                _MEIQIA('manualInit');
                _MEIQIA('init');
                _MEIQIA('hidePanel');
            ` },
      ],
    })

    // zendesk sdk
    // useScriptTag('https://static.zdassets.com/ekr/snippet.js?key=e3a564e7-8188-44c6-890a-a9c30afa19f6', () => {
    //   if (window.zE) {
    //     window.zE('messenger:set', 'zIndex', 100)
    //   }
    // }, {
    //   attrs: {
    //     id: 'ze-snippet',
    //   },
    // })
    // useScriptTag('https://www.googletagmanager.com/gtag/js?id=G-74D9RM9FZ5', () => {
    //   window.dataLayer = window.dataLayer || []
    //   // eslint-disable-next-line prefer-rest-params
    //   window.gtag = () => dataLayer.push(arguments)
    //   window.gtag('js', new Date())
    //   window.gtag('config', 'G-74D9RM9FZ5')
    // })
  }
  if (user.isLogin) {
    user.getUserInfo()
    user.getPartner()
    store.getPrivate({ spotDataState: 1 })
  }

  if (import.meta.client && (route.query.debug || (import.meta.env.VITE_ENV !== 'prod' && isMobile()))) {
    const { default: VConsole } = await import('vconsole')
    window.vconsole = new VConsole()
  }
}

const ThemeSelect = {
  menuBoxShadow: '0px 4px 10px 0px rgba(20, 21, 26, 0.1)',
  peers: {
    InternalSelection: {
      fontSizeTiny: '12px',
      fontSizeSmall: '14px',
      fontSizeMedium: '14px',
      fontSizeLarge: '14px',
      heightTiny: '24px',
      heightSmall: '32px',
      heightMedium: '40px',
      heightLarge: '48px',
      textColor: 'var(--textTextPrimary)',
      color: 'var(--fillInputDefault)',
      colorActive: 'var(--fillInputDefault)',
      caretColor: 'var(--textTextPrimary)',
      border: 'none',
      borderHover: 'none',
      borderActive: '1px solid var(--border)',
      borderFocus: '1px solid var(--border)',
      boxShadowHover: 'none',
      boxShadowActive: 'none',
      boxShadowFocus: 'none',
      placeholderColor: '#414141',
      borderRadius: '8px',
      peers: {
      },
    },
    InternalSelectMenu: {
      color: 'var(--fillPageSecondary)',
      optionFontSizeMedium: '12px',
      optionHeightMedium: '28px',
      paddingSmall: '4px',
      paddingMedium: '4px',
      paddingLarge: '4px',
      paddingHuge: '4px',
      optionColorPending: 'var(--fillPageTertiary)',
      optionColorActive: 'transparent',
      optionColorActivePending: 'var(--fillPageTertiary)',
      optionTextColor: 'var(--textTextSecondary)',
      optionTextColorActive: 'var(--textTextPrimary)',
      optionTextColorPressed: 'var(--textTextPrimary)',
      borderRadius: '4px',
      peers: {

      },
    },
  },
}
export const themeOverrides = {
  common: {
    primaryColor: '#00BE90',
    borderRadius: '0.5rem',
  },
  Switch: {
    boxShadowFocus: 'none',
  },
  Form: {
    labelTextColor: 'var(--textTextPrimary)',
    labelPaddingVertical: '0 0 .5rem 0',
    feedbackTextColorError: '#DF5648',
    feedbackFontSizeMedium: '0.75rem',
    feedbackPadding: '0.5rem 0 1.5rem 0.125rem',
  },
  Input: {
    heightTiny: '32px',
    heightSmall: '36px',
    heightMedium: '40px',
    heightLarge: '48px',
    borderRadius: '8px',
    color: 'var(--fillInputDefault)',
    colorFocus: 'var(--fillInputDefault)',
    colorDisabled: 'var(--fillInputDefault)',
    textColor: 'var(--textTextPrimary)',
    textColorDisabled: 'var(--textTextTertiary)',
    border: 'none',
    borderDisabled: '1px solid var(--fillInputDefault)',
    borderHover: '1px solid var(--specialBtnPrimary)',
    borderError: '1px solid var(--functionalRed3)',
    borderFocusError: '1px solid var(--functionalRed3)',
    colorFocusError: 'var(--fillInputDefault)',
    placeholderColor: 'var(--textTextTertiary)',
    boxShadowFocus: 'none',
    boxShadowFocusWarning: 'none',
    boxShadowFocusError: 'none',
    borderFocus: '1px solid var(--specialBtnPrimary)',
    caretColor: 'var(--specialBtnPrimary)',
    paddingMedium: '0 12px',
  },
  Card: {
    paddingHuge: '24px',
  },
  Button: {
    colorPrimary: 'var(--specialBtnPrimary)',
    colorHoverPrimary: 'var(--specialBtnPrimaryHover)',
    colorFocusPrimary: 'var(--specialBtnPrimaryHover)',
    colorDisabledPrimary: 'var(--specialBtnPrimaryDisable)',
    colorPressedPrimary: 'var(--specialBtnPrimary)',
    borderPrimary: '1px solid var(--specialBtnPrimary)',
    borderDisabledPrimary: '1px solid var(--specialBtnPrimaryDisable)',
    borderHoverPrimary: '1px solid var(--specialBtnPrimary)',
    borderPressedPrimary: '1px solid var(--specialBtnPrimary)',
    borderFocus: '1px solid var(--specialBtnPrimary)',
    borderFocusPrimary: '1px solid var(--specialBtnPrimary)',
    borderHover: '1px solid var(--specialBtnPrimary)',
    heightLarge: '3rem',
    borderRadiusTiny: '9999px',
    borderRadiusSmall: '9999px',
    borderRadiusMedium: '9999px',
    borderRadiusLarge: '9999px',
    rippleDuration: '0',
  },
  Drawer: {
    color: 'var(--bg2)',
    textColor: 'var(--t1)',
  },
  Alert: {
    borderWarning: '1px solid #35251F',
    colorWarning: '#35251F',
    titleTextColorWarning: 'var(--t1)',
    contentTextColorWarning: 'var(--t1)',
  },
  Select: ThemeSelect,
  Popselect: {
    menuBoxShadow: ThemeSelect.menuBoxShadow,
    peers: {
      InternalSelectMenu: ThemeSelect.peers.InternalSelectMenu,
    },
  },
  Tooltip: {
    color: 'var(--specialToast)',
    padding: '0.25rem',
  },
  Slider: {
    indicatorColor: 'var(--bgTooltip)',
  },
  Dropdown: {
    color: '#2B2E34',
    padding: '12px 0',
  },
  Popover: {
    color: 'var(--specialToast)',
    textColor: 'var(--textTextPrimary)',
    padding: '0.75rem',
  },
  Checkbox: {
    textColor: 'var(--textTextSecondary)',
    color: 'transparent',
    colorChecked: 'var(--specialBtnPrimary)',
    fontSizeMedium: '1rem',
    borderRadius: '4px',
    labelLineHeight: '1',
    border: '1px solid var(--lineBorder)',
    boxShadowFocus: 'none',
    borderFocus: '1px solid var(--lineBorder)',
    borderChecked: '1px solid var(--specialBtnPrimary)',
    checkMarkColor: 'var(--textTextOnBtn)',
  },
  Message: {
    color: 'var(--specialToast)',
    textColorInfo: 'var(--textTextSecondary)',
    textColorSuccess: 'var(--textTextSecondary)',
    textColorError: 'var(--textTextSecondary)',
    textColorWarning: 'var(--textTextSecondary)',
    iconMargin: '0 0 0.5rem 0',
    iconSize: '1.5rem',
    fontSize: '1rem',
    maxWidth: '16.5rem',
  },
  Modal: {
    peers: {
      Card: {
        borderRadius: '16px',
        textColor: 'var(--textTextPrimary)',
        closeIconColor: 'var(--textTextPrimary)',
        paddingLarge: '0',
        titleFontSizeLarge: '16px',
        titleFontWeight: 700,
      },
    },
  },
  Pagination: {
    itemTextColorActive: 'var(--textTextWhiteStatic)',
    itemTextColorHover: 'var(--textTextWhiteStatic)',
    itemColorActive: 'var(--themeThemeNormalHover)',
    itemColorActiveHover: 'var(--themeThemeNormalHover)',
    itemColorHover: 'var(--themeThemeNormalHover)',
    itemBorderHover: 'none',
    itemBorderActive: 'none',
    itemBorderRadius: '9999px',
  },
  Steps: {
    stepHeaderFontWeight: 500,
    indicatorTextColorProcess: 'var(--fillPagePrimary)',
    indicatorTextColorWait: 'var(--textTextTertiary)',
    indicatorTextColorFinish: 'var(--textTextPrimary)',
    indicatorTextColorError: 'var(--error)',
    indicatorBorderColorProcess: 'var(--textTextPrimary)',
    indicatorBorderColorWait: 'var(--textTextTertiary)',
    indicatorBorderColorFinish: 'var(--textTextPrimary)',
    indicatorBorderColorError: 'var(--error)',
    indicatorColorProcess: 'var(--textTextPrimary)',
    indicatorColorWait: 'none',
    indicatorColorFinish: 'none',
    indicatorColorError: 'none',
    splitorColorProcess: 'var(--textTextPrimary)',
    splitorColorWait: 'var(--textTextTertiary)',
    splitorColorFinish: 'var(--textTextPrimary)',
    splitorColorError: 'var(--error)',
    headerTextColorProcess: 'var(--textTextPrimary)',
    headerTextColorWait: 'var(--textTextTertiary)',
    headerTextColorFinish: 'var(--textTextPrimary)',
    headerTextColorError: 'var(--error)',
    descriptionTextColorProcess: 'var(--textTextPrimary)',
    descriptionTextColorWait: 'var(--textTextTertiary)',
    descriptionTextColorFinish: 'var(--textTextTertiary)',
    descriptionTextColorError: 'var(--error)',
    stepHeaderFontSizeSmall: '16px',
    stepHeaderFontSizeMedium: '18px',
    // indicatorIndexFontSizeSmall: string;
    // indicatorIndexFontSizeMedium: string;
    // indicatorSizeSmall: string,
    indicatorSizeMedium: '24px',
    indicatorIconSizeSmall: '20px',
    // indicatorIconSizeMedium: string;
  },
  Scrollbar: {
    color: 'var(--specialSwitchFill)',
    railInsetVerticalRight: '2px 8px 2px auto',
    width: '3px',
    height: '3px',
  },
}
